export enum BookingStatus {
  PENDING = 'pending',
  CONFIRMED = 'confirmed',
  IN_PROGRESS = 'in-progress',
  COMPLETED = 'completed',
  CANCELLED = 'cancelled',
  NO_SHOW = 'no-show',
  MODIFIED = 'modified'
}

export enum PaymentStatus {
  PENDING = 'pending',
  PAID = 'paid',
  PARTIAL = 'partial',
  REFUNDED = 'refunded',
  FAILED = 'failed'
}

export interface Customer {
  _id: string;
  customerNumber?: string; // Format: 00001-FEB-2026
  firstName: string;
  lastName: string;
  email: string;
  phone?: string; // Legacy field
  contact?: string; // Current field in database
  dateOfBirth?: Date;
  licenseNumber?: string;
  licenseExpiryDate?: Date;
  address?: {
    street: string;
    city: string;
    state: string;
    zipCode: string;
    country: string;
  };
}

export interface BookingImage {
  type: 'pickup' | 'return' | 'damage' | 'other';
  url: string;
  description?: string;
  uploadedDate: Date;
  uploadedBy?: string;
}

export interface BookingDocument {
  name: string;
  url: string;
  uploadedDate: Date;
  type: 'contract' | 'license' | 'insurance' | 'receipt' | 'other';
}

export interface StatusHistoryEntry {
  date: Date;
  fromStatus: string;
  toStatus: string;
  reason?: string;
  modifiedBy?: string;
  notes?: string;
}

export interface ModificationHistoryEntry {
  date: Date;
  field: string;
  oldValue: string;
  newValue: string;
  modifiedBy?: string;
  reason?: string;
}

export interface EmergencyContact {
  name: string;
  phone: string;
  relationship: string;
}

export interface Booking {
  _id: string;
  customer: Customer;
  car: any; // Will be populated with Car data
  rate: any; // Will be populated with Rate data
  extras: any[]; // Will be populated with Extra data
  
  pickUpLocation: string;
  pickUpDate: Date;
  pickUpTime: string;
  returnLocation: string;
  returnDate: Date;
  returnTime: string;
  driverAge: number;
  agreeToTerms: boolean;
  
  status: BookingStatus;
  paymentStatus: PaymentStatus;
  bookingReference: string;
  totalAmount: number;
  paidAmount: number;
  refundAmount: number;
  paymentMethod?: string;
  paymentTransactionId?: string;
  stripePaymentIntentId?: string;
  
  statusHistory: StatusHistoryEntry[];
  modificationHistory: ModificationHistoryEntry[];
  
  specialRequests?: string;
  internalNotes?: string;
  customerNotes?: string;
  
  actualPickUpTime?: Date;
  actualReturnTime?: Date;
  startMileage?: number;
  endMileage?: number;
  totalMilesDriven?: number;
  fuelLevelStart?: string;
  fuelLevelEnd?: string;
  
  lateFees?: number;
  damageFees?: number;
  cleaningFees?: number;
  additionalCharges?: number;
  additionalChargesDescription?: string;
  
  images: BookingImage[];
  documents: BookingDocument[];
  
  requiresApproval: boolean;
  approvedBy?: string;
  approvedDate?: Date;
  rejectionReason?: string;
  
  isRecurring: boolean;
  recurringPattern?: string;
  parentBookingId?: string;
  
  discountAmount?: number;
  discountCode?: string;
  discountDescription?: string;
  taxAmount?: number;
  insuranceAmount?: number;
  
  emergencyContact?: EmergencyContact;
  
  hasConflict: boolean;
  conflictReason?: string;
  conflictResolvedDate?: Date;
  conflictResolvedBy?: string;
  
  customerRating?: number;
  customerFeedback?: string;
  feedbackDate?: Date;
  
  sessionId?: string;
  conversionSource?: string;
  analyticsMetadata?: Record<string, any>;
  
  createdAt: Date;
  updatedAt: Date;
}

export interface BookingFilters {
  status?: BookingStatus;
  paymentStatus?: PaymentStatus;
  startDate?: Date;
  endDate?: Date;
  customerId?: string;
  carId?: string;
  hasConflict?: boolean;
  requiresApproval?: boolean;
  search?: string;
}

export interface BookingListParams extends BookingFilters {
  page?: number;
  limit?: number;
  lightweight?: boolean;
}

export interface BookingListResponse {
  items: Booking[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

export interface AdminUpdateBookingDto {
  status?: BookingStatus;
  paymentStatus?: PaymentStatus;
  pickUpDate?: string;
  pickUpTime?: string;
  pickUpLocation?: string;
  returnDate?: string;
  returnTime?: string;
  returnLocation?: string;
  totalAmount?: number;
  paidAmount?: number;
  refundAmount?: number;
  paymentMethod?: string;
  paymentTransactionId?: string;
  specialRequests?: string;
  internalNotes?: string;
  customerNotes?: string;
  actualPickUpTime?: string;
  actualReturnTime?: string;
  startMileage?: number;
  endMileage?: number;
  fuelLevelStart?: string;
  fuelLevelEnd?: string;
  lateFees?: number;
  damageFees?: number;
  cleaningFees?: number;
  additionalCharges?: number;
  additionalChargesDescription?: string;
  requiresApproval?: boolean;
  approvedBy?: string;
  approvedDate?: string;
  rejectionReason?: string;
  discountAmount?: number;
  discountCode?: string;
  discountDescription?: string;
  taxAmount?: number;
  insuranceAmount?: number;
  emergencyContact?: EmergencyContact;
  hasConflict?: boolean;
  conflictReason?: string;
  conflictResolvedBy?: string;
  customerRating?: number;
  customerFeedback?: string;
  bookingReference?: string;
}

export interface UpdateBookingStatusDto {
  status: BookingStatus;
  reason?: string;
  modifiedBy?: string;
  notes?: string;
}

export interface ProcessPaymentDto {
  amount: number;
  paymentMethod: string;
  transactionId?: string;
  stripePaymentIntentId?: string;
  paymentStatus?: PaymentStatus;
}

export interface BookingSummary {
  totalBookings: number;
  pendingBookings: number;
  confirmedBookings: number;
  inProgressBookings: number;
  completedBookings: number;
  cancelledBookings: number;
  totalRevenue: number;
  pendingPayments: number;
  conflictedBookings: number;
  bookingsRequiringApproval: number;
}
